import { PathPickProduct } from "@/features/pathpick/types";
import { LineSheetProduct } from "@/features/line-sheet-sets/line-sheet-set.types";
import {
  LineSheetProductMarketPrice,
  LineSheetSetMarketPricing,
} from "@/features/order-sheet-sets/hooks/use-line-sheet-set-market-prices";
import { ProductPrice } from "@/features/types";
import {
  getMarketPriceByBasis,
  getPathPickMargin,
  getPathPickMarketExternalUrl,
  getPathPickMarketPrice,
  PathPickMargin,
} from "@/features/pathpick/utils/market";
import {
  PathPickMarket,
  PathPickMarketPriceBasis,
} from "@/features/pathpick/types";

// 오더시트의 비교 기준 — 패스픽의 기본값과 같은 '최저 입찰가'. 오더시트에는 기준을 고르는
// 필터가 없어 하나로 고정한다(사이즈별 시세 모달 안에서는 거래가로 바꿔 볼 수 있다).
// 그리드 시세 열과 카드 비교 블록이 같은 상수를 써야 두 보기의 숫자가 같다.
export const ORDER_SHEET_MARKET_BASIS: PathPickMarketPriceBasis = "bidPrice";

/**
 * 예상 매입가 — 화면의 현재 공급가(세트 통화)로 계산한다.
 *
 *   (공급가 × (1 + uplift) × 환율) 을 천원 단위로 반올림
 *   uplift = 운송비 × 관세 × 부가세 × 우노 수수료(바이어별, 부가세 포함) 를 곱한 계수 — 서버가 상품별로 준다
 *
 * 패스픽 핫딜의 판매가 식과 일부러 다르다: 발주 수수료 대신 바이어별 우노 수수료를 얹고,
 * 정액 수수료(통관수수료·국내배송비·박스비)는 넣지 않는다. 그래서 같은 상품이라도 패스픽 목록의
 * 판매가와 같은 숫자가 아니다. 서버 값으로 받지 않는 이유: 바이어가 컨디션을 고치면 공급가가
 * 이 화면에서 바뀌고, 그때 예상 매입가와 마진도 같이 바뀌어야 한다.
 * 공급가가 없거나 그 통화의 환율이 없으면 null(호출부는 "-" 로 비운다).
 */
type UpliftSource = Pick<LineSheetProductMarketPrice, "uplift"> &
  Partial<Pick<LineSheetProductMarketPrice, "upliftFactors">>;

export function calcEstimatedSupplyKrw(
  supplyPrice: ProductPrice | null | undefined,
  marketPrice: UpliftSource | null | undefined,
  pricing: LineSheetSetMarketPricing | null | undefined
): number | null {
  return (
    getEstimatedSupplyBreakdown(supplyPrice, marketPrice, pricing)?.value ?? null
  );
}

/** 예상 매입가와 그 계산에 들어간 재료 — 값에 올렸을 때 보여주는 계산식이 이걸 그대로 쓴다. */
export interface EstimatedSupplyBreakdown {
  // 공급가(세트 통화)
  amount: number;
  currency: string;
  // 부대비용 계수 — (1 + uplift) 를 곱한다. 계산은 이 값 하나로 한다
  uplift: number;
  // 계수의 구성(운송비·관세·부가세·우노 수수료) — 식을 풀어 보여줄 때만 쓴다. 서버가 안 주면 빈 목록
  upliftFactors: { key: string; rate: number }[];
  // 그 통화의 원화 환율(원화 시트는 1)
  rate: number;
  // 결과 — 천원 단위로 반올림한 예상 매입가
  value: number;
}

export function getEstimatedSupplyBreakdown(
  supplyPrice: ProductPrice | null | undefined,
  marketPrice: UpliftSource | null | undefined,
  pricing: LineSheetSetMarketPricing | null | undefined
): EstimatedSupplyBreakdown | null {
  if (!supplyPrice?.amount || !marketPrice || !pricing) {
    return null;
  }
  const rate =
    supplyPrice.currency === "KRW" ? 1 : pricing.rates?.[supplyPrice.currency];
  if (!rate) {
    return null;
  }
  const uplift = marketPrice.uplift ?? 0;
  const krw = supplyPrice.amount * (1 + uplift) * rate;
  return {
    amount: supplyPrice.amount,
    currency: supplyPrice.currency,
    uplift,
    upliftFactors: marketPrice.upliftFactors ?? [],
    rate,
    // 패스픽 서버의 int(x / 1000 + 0.5) * 1000 과 같은 반올림(백원 자리에서 올림/버림).
    value: Math.floor(krw / 1000 + 0.5) * 1000,
  };
}

/**
 * 오더시트 상품 + 시세 → 패스픽 상품 모양.
 *
 * 패스픽의 사이즈별 시세 모달·마진 산식 모달은 PathPickProduct 를 받는다. 오더시트 상품은
 * 필드 이름만 다를 뿐 같은 정보(브랜드·SKU·사이즈별 재고·시세)를 갖고 있어서, 화면을 새로
 * 만들지 않고 모양만 맞춰 그 모달을 그대로 연다. 여기서 salePrice 는 예상 매입가
 * (calcEstimatedSupplyKrw)다 — 패스픽 핫딜과 같은 식이라 두 화면의 예상 마진 기준이 같다.
 */
export function toMarketProduct(
  product: LineSheetProduct,
  marketPrice: LineSheetProductMarketPrice,
  estimatedKrw: number | null
): PathPickProduct {
  // 사이즈 매칭 키는 패스픽과 같은 서버 정규화 표기(display_name). 오더시트 재고 목록의 원문
  // ("EU 40", "36½")을 그대로 쓰면 크림의 "40", "36.5" 와 못 만난다. 없으면 원문 그대로.
  const displayNameByName = new Map(
    (marketPrice.options ?? []).map((option) => [option.name, option.displayName])
  );
  const displayName = (name: string) => displayNameByName.get(name) || name;
  return {
    boutiqueId: -1,
    id: product.id,
    productType: "LINE_SHEET_PRODUCT",
    brandName: product.brandName ?? "",
    sku: product.sku ?? product.modelNumber ?? "",
    name: product.name ?? "",
    categories: product.categories ?? [],
    pathradeCategory: product.categories?.[0] ?? "",
    deliveryType: "",
    season: product.season ?? "",
    color: [product.colorCode, product.colorName].filter(Boolean).join(" "),
    material: product.material ?? "",
    gender: product.gender ?? "",
    imageUrls: [
      ...(product.imageUrls ?? []),
      ...(product.alternativeImageUrls ?? []),
    ],
    salePrice: { currency: "KRW", value: estimatedKrw ?? 0 },
    madeIn: "",
    marketPrice: { currency: "KRW", amount: 0 },
    // 사이즈별 재고 — 모달의 옵션 표가 마켓 옵션과 이름으로 맞춘다.
    options: (product.stockQuantityWithOptionList ?? []).map((option) => ({
      name: option.name,
      displayName: displayName(option.name),
      stock: option.quantity,
    })),
    sizes: (product.stockQuantityWithOptionList ?? []).map((option) => ({
      name: option.name,
      stock: option.quantity,
    })),
    lineSheetProductSku: product.sku,
    isFavorite: false,
    isNew: false,
    isInBasket: false,
    requests: null,
    isBuyerOrdered: false,
    kream: marketPrice.kream ?? null,
    poizon: marketPrice.poizon ?? null,
  };
}

/** 마켓 1개의 셀 표시값 — 기준가는 최저 입찰가(패스픽 카드의 기본 기준). 그 값이 없으면 "-". */
export interface OrderSheetMarketCell extends PathPickMargin {
  market: PathPickMarket;
  basisValue: number | null;
  salesCount: number | null;
  externalUrl: string | null;
}

export function toMarketCell(
  marketPrice: LineSheetProductMarketPrice | undefined,
  market: PathPickMarket,
  // 예상 매입가(calcEstimatedSupplyKrw). 없으면 시세만 있고 마진은 비운다.
  estimatedKrw: number | null
): OrderSheetMarketCell {
  const empty: OrderSheetMarketCell = {
    market,
    basisValue: null,
    salesCount: null,
    externalUrl: null,
    expectedMargin: null,
    marginRate: null,
  };
  if (!marketPrice) {
    return empty;
  }
  const price = getPathPickMarketPrice(marketPrice, market);
  const basisValue = getMarketPriceByBasis(price, ORDER_SHEET_MARKET_BASIS);
  // 최저 입찰가만 없는 매칭 상품도 있다 — 값은 "-" 지만 마켓으로 나가는 링크는 살려 둔다.
  const externalUrl = getPathPickMarketExternalUrl(
    { kream: marketPrice.kream, poizon: marketPrice.poizon, sku: "" },
    market
  );
  if (basisValue == null) {
    return { ...empty, externalUrl };
  }
  const margin =
    estimatedKrw != null
      ? getPathPickMargin(basisValue, estimatedKrw)
      : { expectedMargin: null, marginRate: null };
  return {
    market,
    basisValue,
    salesCount:
      (market === "KREAM"
        ? marketPrice.kream?.salesCount
        : marketPrice.poizon?.sales_30d) ?? null,
    // 패스픽 카드와 같은 주소 — 크림은 상품 페이지, 포이즌은 셀러센터 입찰 등록 화면.
    // (포이즌은 호출부가 안내 모달을 거쳐 연다 — useMarketExternalLink)
    externalUrl,
    ...margin,
  };
}
